<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { i18n } from '@/i18n';
import Calendar from '@/corePagesModule/components/Calendar.vue';
import Mdi from '@/corePagesModule/components/Mdi.vue';
import TextButton from '@/components/input/Btn/TextButton.vue';

const props = withDefaults(
  defineProps<{
    label: string;
    modelValue?: string[];
    includeNull?: boolean;
    hideTime?: boolean;
  }>(),
  {
    /**
     * モデル値のデフォルト。
     */
    modelValue: () => [],
    includeNull: false,
    hideTime: false,
  }
);

const emit = defineEmits<{
  (e: 'update:modelValue', value: string[]): void;
  (e: 'update:includeNull', value: boolean): void;
}>();

const t = i18n.t;

/**
 * クエリで受け取ったミリ秒文字列をDate型に変換する。
 * @param target - 変換対象の文字列
 */
const convertToDate = (target?: string): Date | null => {
  if (!target) return null;
  const parsed = Number(target);
  if (Number.isNaN(parsed)) return null;
  return new Date(parsed);
};

/**
 * 日時を表示用の文字列に整形する。
 * @param target - 整形対象のDate
 */
const formatDateTime = (target: Date | null): string => {
  if (!target) return '';
  const year = target.getFullYear();
  const month = `${target.getMonth() + 1}`.padStart(2, '0');
  const day = `${target.getDate()}`.padStart(2, '0');
  if (props.hideTime) return `${year}/${month}/${day}`;
  const hour = `${target.getHours()}`.padStart(2, '0');
  const minute = `${target.getMinutes()}`.padStart(2, '0');
  const second = `${target.getSeconds()}`.padStart(2, '0');
  return `${year}/${month}/${day} ${hour}:${minute}:${second}`;
};

const isOpen = ref(false);
const activeField = ref<'from' | 'to'>('from');
const startDate = ref<Date | null>(convertToDate(props.modelValue?.[0]));
const endDate = ref<Date | null>(convertToDate(props.modelValue?.[1]));
const includeNull = ref(props.includeNull);
const calendarValue = ref<Date | null>(startDate.value ?? endDate.value ?? null);
const calendarRenderKey = ref(0);
const dropdownRef = ref<HTMLElement | null>(null);
const triggerRef = ref<HTMLElement | null>(null);
const startFieldRef = ref<HTMLElement | null>(null);
const endFieldRef = ref<HTMLElement | null>(null);
const panelRef = ref<HTMLElement | null>(null);
const fieldsRef = ref<HTMLElement | null>(null);
const panelStyle = ref<Record<string, string>>({});
const calendarRef = ref<HTMLElement | null>(null);
const calendarStyle = ref<Record<string, string>>({});
const isCalendarVisible = ref(false);

/**
 * 選択中の開始・終了日時を親へ送出する。
 */
const emitModelValue = () => {
  if (!startDate.value && !endDate.value) {
    emit('update:modelValue', []);
    return;
  }
  const payload: string[] = ['', ''];
  if (startDate.value) payload[0] = startDate.value.getTime().toString();
  if (endDate.value) payload[1] = endDate.value.getTime().toString();
  emit('update:modelValue', payload);
};

watch(
  () => props.modelValue,
  (value) => {
    startDate.value = convertToDate(value?.[0]);
    endDate.value = convertToDate(value?.[1]);
    calendarValue.value = activeField.value === 'from' ? startDate.value : endDate.value;
  }
);

watch(
  () => props.includeNull,
  (value) => {
    includeNull.value = value;
  }
);

watch(activeField, (field) => {
  calendarValue.value = field === 'from' ? startDate.value : endDate.value;
  nextTick(updatePositions);
});

const displayText = computed(() => {
  const [start, end] = props.modelValue ?? [];
  const startFromModel = convertToDate(start);
  const endFromModel = convertToDate(end);
  if (startFromModel && endFromModel) {
    return `${formatDateTime(startFromModel)} - ${formatDateTime(endFromModel)}`;
  }
  if (startFromModel) return formatDateTime(startFromModel);
  if (endFromModel) return formatDateTime(endFromModel);
  return '';
});

/**
 * 開始日時選択ボタンを押した時の処理。
 */
const selectStart = () => {
  activeField.value = 'from';
  calendarValue.value = startDate.value;
  isOpen.value = true;
  isCalendarVisible.value = true;
  nextTick(updatePositions);
};

/**
 * 終了日時選択ボタンを押した時の処理。終了日時が未設定なら当月の空状態で開く。
 */
const selectEnd = () => {
  activeField.value = 'to';
  calendarValue.value = endDate.value;
  if (!endDate.value) {
    calendarRenderKey.value += 1;
  }
  isOpen.value = true;
  isCalendarVisible.value = true;
  nextTick(updatePositions);
};

/**
 * カレンダー選択値を適用する。
 * @param value - 選択された日時
 */
/**
 * カレンダーから受け取った日時を開始・終了に反映する。
 * @param value - 選択された日時
 */
const applyCalendarSelection = (value: Date | null) => {
  if (activeField.value === 'from') {
    if (props.hideTime && value) {
      value.setHours(0, 0, 0, 0);
    }
    startDate.value = value;
    if (value && endDate.value && value.getTime() > endDate.value.getTime()) {
      if (props.hideTime) {
        const cloned = new Date(value);
        cloned.setHours(23, 59, 59, 999);
        endDate.value = cloned;
      } else {
        endDate.value = value;
      }
    }
  } else {
    if (props.hideTime && value) {
      value.setHours(23, 59, 59, 999);
    }
    endDate.value = value;
    if (value && startDate.value && value.getTime() < startDate.value.getTime()) {
      if (props.hideTime) {
        const cloned = new Date(value);
        cloned.setHours(0, 0, 0, 0);
        startDate.value = cloned;
      } else {
        startDate.value = value;
      }
    }
  }
  emitModelValue();
};

/**
 * カレンダーのv-model更新を受け取る。
 * @param value - 選択された日時
 */
const onUpdateCalendar = (value: Date | null) => {
  applyCalendarSelection(value);
};

/**
 * 入力を全てクリアする。
 */
const clearRange = () => {
  startDate.value = null;
  endDate.value = null;
  calendarValue.value = null;
  includeNull.value = false;
  emitModelValue();
  emit('update:includeNull', false);
  isOpen.value = false;
  isCalendarVisible.value = false;
};

/**
 * 選択内容を親へ反映する。
 */
const applyRange = () => {
  emitModelValue();
  isOpen.value = false;
  isCalendarVisible.value = false;
};

/**
 * date-range-filter__fieldsとカレンダー以外をクリックしたら閉じる。
 * @param event - クリックイベント
 */
const handleClickOutside = (event: MouseEvent) => {
  if (!isOpen.value) return;
  const target = event.target;
  if (!(target instanceof Node)) return;
  if (dropdownRef.value?.contains(target)) return;
  if (panelRef.value?.contains(target)) return;
  if (fieldsRef.value?.contains(target)) return;
  if (calendarRef.value?.contains(target)) return;
  if (triggerRef.value?.contains(target)) return;
  isOpen.value = false;
  isCalendarVisible.value = false;
};

/**
 * トリガーの下にパネルを配置する。
 */
const updatePanelPosition = () => {
  if (!isOpen.value) return;
  if (!triggerRef.value) return;
  const rect = triggerRef.value.getBoundingClientRect();
  panelStyle.value = {
    top: `${rect.bottom + window.scrollY + 8}px`,
    left: `${rect.left + window.scrollX}px`,
  };
};

/**
 * フィールドの右側にカレンダーを配置する。
 */
const updateCalendarPosition = () => {
  if (!isCalendarVisible.value) return;
  const anchor = activeField.value === 'from' ? startFieldRef.value : endFieldRef.value;
  if (!anchor) return;
  const rect = anchor.getBoundingClientRect();
  calendarStyle.value = {
    top: `${rect.top + window.scrollY}px`,
    left: `${rect.right + window.scrollX + 8}px`,
  };
};

/**
 * パネルとカレンダーの表示位置を再計算する。
 */
const updatePositions = () => {
  updatePanelPosition();
  updateCalendarPosition();
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  window.addEventListener('resize', updatePositions);
  window.addEventListener('scroll', updatePositions, true);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
  window.removeEventListener('resize', updatePositions);
  window.removeEventListener('scroll', updatePositions, true);
});

/**
 * トリガー入力の開閉をトグルする。
 */
const toggleDropdown = () => {
  if (isOpen.value) {
    isOpen.value = false;
    isCalendarVisible.value = false;
    return;
  }
  activeField.value = 'from';
  calendarValue.value = startDate.value ?? endDate.value ?? null;
  isOpen.value = true;
  isCalendarVisible.value = false;
  nextTick(updatePositions);
};

/**
 * 未設定のデータを含めるかを切り替える。
 * @param event - クリックイベント
 */
const onToggleIncludeNull = (event: Event) => {
  const target = event.target;
  if (!(target instanceof HTMLInputElement)) return;
  includeNull.value = target.checked;
  emit('update:includeNull', target.checked);
};
</script>

<template>
  <div ref="dropdownRef" class="date-range-filter">
    <div class="date-range-filter__label">{{ label }}</div>
    <button ref="triggerRef" type="button" class="date-range-filter__trigger" @click="toggleDropdown">
      <span v-if="displayText" class="date-range-filter__value">{{ displayText }}</span>
      <span v-else class="date-range-filter__placeholder">{{ t('選択してください') }}</span>
      <Mdi class="date-range-filter__trigger-icon" :class="{ 'date-range-filter__trigger-icon--open': isOpen }" icon="expand_more" />
    </button>
    <Teleport to="body">
      <div v-if="isOpen" ref="panelRef" class="date-range-filter__panel" :style="panelStyle">
        <div class="date-range-filter__body">
          <div ref="fieldsRef" class="date-range-filter__fields">
            <div class="date-range-filter__field" :class="{ 'date-range-filter__field--active': isOpen && activeField === 'from' }">
              <div class="date-range-filter__field-label">{{ t(hideTime ? 'FilterStartDate' : 'FilterStartDateTime') }}</div>
              <button ref="startFieldRef" type="button" class="date-range-filter__field-input" @click="selectStart">
                <span class="date-range-filter__field-text">
                  {{ startDate ? formatDateTime(startDate) : t('選択してください') }}
                </span>
                <Mdi icon="calendar_today" class="date-range-filter__field-icon" />
              </button>
            </div>
            <div class="date-range-filter__field" :class="{ 'date-range-filter__field--active': isOpen && activeField === 'to' }">
              <div class="date-range-filter__field-label">{{ t(hideTime ? 'FilterEndDate' : 'FilterEndDateTime') }}</div>
              <button ref="endFieldRef" type="button" class="date-range-filter__field-input" @click="selectEnd">
                <span class="date-range-filter__field-text">
                  {{ endDate ? formatDateTime(endDate) : t('選択してください') }}
                </span>
                <Mdi icon="calendar_today" class="date-range-filter__field-icon" />
              </button>
            </div>
          </div>
          <label class="date-range-filter__null-option">
            <input class="date-range-filter__null-option-checkbox" type="checkbox" :checked="includeNull" @change="onToggleIncludeNull" />
            <span class="date-range-filter__null-option-text">{{
              t(hideTime ? 'FilterIncludeNullDate' : 'FilterIncludeNullLastCheckedAt')
            }}</span>
          </label>
        </div>
        <div class="date-range-filter__actions">
          <TextButton :button-style="'btn-secondary'" :size="'btn-sm'" :on-click="clearRange">
            {{ t('FilterClear') }}
          </TextButton>
          <TextButton :button-style="'btn-primary'" :size="'btn-sm'" :on-click="applyRange">
            {{ t('FilterApply') }}
          </TextButton>
        </div>
      </div>
    </Teleport>
    <Teleport to="body">
      <div v-if="isCalendarVisible" ref="calendarRef" class="date-range-filter__calendar-float" :style="calendarStyle">
        <Calendar :key="calendarRenderKey" :model-value="calendarValue" :hide-time="hideTime" @update:model-value="onUpdateCalendar" />
      </div>
    </Teleport>
  </div>
</template>

<style scoped lang="scss">
.date-range-filter {
  display: grid;
  grid-template-rows: auto auto;
  position: relative;
  width: 100%;
  box-sizing: border-box;

  &__label {
    display: grid;
    grid-template-columns: auto auto;
    gap: 3px;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    font-family: Noto Sans JP;
    font-size: 14px;
    font-weight: 400;
    line-height: 18.2px;
    text-align: center;

    color: $textDefault;
  }

  &__trigger {
    width: 100%;
    border: 1px solid $borderDefault;
    border-radius: 4px;
    padding: 6px 8px;
    background-color: $semanticWhite;
    display: grid;
    grid-template-columns: 1fr 24px;
    align-items: center;
    cursor: pointer;
    overflow: hidden;
    box-sizing: border-box;
    height: 40px;
    width: 160px;
  }

  &__value {
    color: $textDefault;
    height: 20px;
    font-family: Noto Sans JP;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0em;
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__placeholder {
    color: $textSecondary;
    font-family: Noto Sans JP;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0em;
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__trigger-icon {
    color: $textDefault;
    display: grid;
    place-items: center;
    font-size: 24px;
    color: $textSecondary;

    &--open {
      transform: rotate(180deg);
    }
  }

  &__fields {
    display: grid;
    grid-template-columns: 1fr;
    row-gap: 12px;
  }

  &__null-option {
    display: grid;
    grid-template-columns: 16px 1fr;
    column-gap: 8px;
    align-items: center;
    margin-top: 12px;
  }

  &__null-option-checkbox {
    width: 16px;
    height: 16px;
    border-radius: 2px;
    border-color: $gray30;
  }

  &__null-option-text {
    color: $textDefault;
    font-size: 14px;
    line-height: 18px;
  }

  &__field {
    display: grid;
    grid-template-rows: auto auto;
    row-gap: 6px;

    &--active {
      & .date-range-filter__field-input {
        border-color: $borderActive;
        box-shadow: 0 0 0 1px $borderActive inset;
      }
    }

    &-label {
      color: $textDefault;
      font-size: 14px;
      font-weight: 400;
      line-height: 18px;
    }

    &-input {
      width: 100%;
      border: 1px solid $borderDefault;
      border-radius: 4px;
      padding: 6px 8px;
      background-color: $semanticWhite;
      display: grid;
      grid-template-columns: 1fr 20px;
      align-items: center;
      column-gap: 8px;
      cursor: pointer;
    }

    &-text {
      color: $textDefault;
      font-size: 14px;
      font-weight: 400;
      line-height: 20px;
      text-align: left;
    }

    &-icon {
      color: $textDefault;
      display: grid;
      place-items: center;
    }
  }

  &__panel {
    position: absolute;
    background: $semanticWhite;
    border: 1px solid $borderDefault;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    border-radius: 4px;
    padding: 12px;
    display: grid;
    grid-template-rows: auto auto;
    row-gap: 12px;
    z-index: 12;
    min-width: 240px;
  }

  &__body {
    display: grid;
    grid-template-columns: 1fr;
    align-items: start;
  }

  &__actions {
    display: grid;
    grid-template-columns: repeat(2, auto);
    justify-content: end;
    column-gap: 8px;
  }

  &__calendar-float {
    position: absolute;
    background: $semanticWhite;
    border: 1px solid $borderDefault;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.12);
    border-radius: 4px;
    padding: 12px;
    display: grid;
    min-width: 320px;
    z-index: 12;
  }
}
</style>
